import React, { useEffect, useState } from 'react';
import { layout, palette, styleUtils } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { last } from 'lodash';
import { useFormFactor } from 'App/Header/useFormFactor';
import { MobileLoaderSheet } from 'App/ProgressAlert/MobileLoaderSheet';
import { ResourceAlert } from 'App/ProgressAlert/ResourceAlert';
import { usePreventBrowserClose } from 'App/ProgressAlert/usePreventBrowserClose';
import { WaitAlert } from 'App/ProgressAlert/WaitAlert';
import { isDesktopBrowser, isMobileBrowser } from 'Environment/launchResource/device';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';

const StyledDiv = styled.div`
	display: flex;
	flex-direction: column;
	margin: 25px;
	position: absolute;
	right: 0;
	bottom: 0;
	z-index: 4;
	box-shadow: ${styleUtils.shadow3.boxShadow};
	background-color: ${palette.white};
	border-radius: ${layout.smallSpace};
	width: 468px;
	max-height: 50vh;
`;

const AlertsContainer = styled.div`
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	scrollbar-width: thin;
`;

const StyledSheetDiv = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	/* stylelint-disable-next-line unit-no-unknown */
	height: 100dvh;
	background-color: rgba(0, 0, 0, 0.4);
	z-index: 10;
`;

export function LaunchProgressWindow() {
	const { value } = useLoadableResourceContext();
	const { resources, launchProgress, updateSession } = value;
	const { isLargeFormFactor } = useFormFactor();
	const [expandedOperationId, setExpandedOperationId] = useState<string | null>(null);
	const { showWait } = usePreventBrowserClose(launchProgress);
	const activeLaunchIds = Object.keys(launchProgress);

	useEffect(() => {
		const operationIds = Object.keys(launchProgress);
		if (operationIds.length > 0) {
			const mostRecentOpId = operationIds[operationIds.length - 1];
			const mostRecentLaunch = launchProgress[mostRecentOpId];
			if (mostRecentLaunch && mostRecentLaunch.error) {
				setExpandedOperationId(mostRecentOpId);
			}
		} else {
			setExpandedOperationId(null);
		}
	}, [launchProgress]);

	const handleCancel = (operationId: string) => {
		const launchDetails = launchProgress[operationId];
		launchDetails.onCancel();
		updateSession(state => ({
			launchProgress: {
				...state.launchProgress,
				[operationId]: {
					...state.launchProgress[operationId],
					status: LaunchStatus.Cancelling,
				},
			},
		}));
	};

	const handleClose = (closeOperationId: string) => {
		activeLaunchIds.forEach(operationId => {
			operationId === closeOperationId && delete launchProgress[operationId];
			updateSession(state => ({
				launchProgress: {
					...state.launchProgress,
				},
			}));
		});
	};

	const clearAllSessions = () => {
		updateSession({
			launchProgress: {},
		});
	};

	const showSheet = () => {
		const progressPair = last(Object.entries(launchProgress));
		const operationId = progressPair ? progressPair[0] : '';
		const latestLaunchProgress = progressPair ? progressPair[1] : null;
		return (
			latestLaunchProgress && (
				<StyledSheetDiv>
					<MobileLoaderSheet
						key={operationId}
						operationId={operationId}
						launchProgressContext={latestLaunchProgress}
						onCancel={handleCancel}
						onClose={handleClose}
					/>
				</StyledSheetDiv>
			)
		);
	};

	return (
		<>
			{launchProgress &&
				activeLaunchIds.length > 0 &&
				isLargeFormFactor &&
				isDesktopBrowser() && (
					<StyledDiv>
						<>
							<AlertsContainer>
								{activeLaunchIds.map((operationId, index) => (
									<ResourceAlert
										key={operationId}
										isLast={index === activeLaunchIds?.length - 1}
										error={launchProgress[operationId]?.error}
										operationId={operationId}
										onCancel={handleCancel}
										onClose={handleClose}
										resource={resources.find(
											({ id }) => id === launchProgress[operationId].resourceId
										)}
										isExpanded={expandedOperationId === operationId}
										onToggleExpand={() =>
											setExpandedOperationId(
												expandedOperationId === operationId ? null : operationId
											)
										}
										launchProgressContext={launchProgress[operationId]}
									/>
								))}
							</AlertsContainer>
							{(activeLaunchIds?.length > 0 || showWait) && (
								<WaitAlert
									onClose={clearAllSessions}
									activeLaunchsCount={
										Object.values(launchProgress).filter(
											item => item.status !== LaunchStatus.Failed
										).length
									}
								/>
							)}
						</>
					</StyledDiv>
				)}
			{!isLargeFormFactor && isMobileBrowser() && showSheet()}
		</>
	);
}
